export interface JSONApiError {
  code?: string;
  title?: string;
  detail?: string;
  status?: string | number;
  source?: { pointer?: string };
  meta?: string;
}

export type JSONApiErrors = JSONApiError[];

type NestedJSONApiErrors = { _error: JSONApiError[] };
// where keys are Resource property fields
// for use with forms / validation errors
export interface FormCompatibleError<
  T = string | string[] | JSONApiError | JSONApiError[]
> {
  _error?: T;
  [key: string]: T;
}

export type ErrorTypes =
  | string
  | JSONApiErrors
  | FormCompatibleError
  | NestedJSONApiErrors[];

export const getErrorData = (
  errorBody: ErrorTypes,
  key: keyof JSONApiError
) => {
  if (!errorBody) return [];

  // handle string type error bodies
  if (typeof errorBody === 'string') return [errorBody];

  // handle JSONApi compatible error bodies
  if (Array.isArray(errorBody)) {
    if ((errorBody as JSONApiError[])[0][key]) {
      return errorBody.map((e) => e[key]);
    } else {
      return getErrorData((errorBody as NestedJSONApiErrors[])[0], key);
    }
  }

  // handle redux form compatible error bodies
  try {
    if (Object.getPrototypeOf(errorBody) === Object.prototype) {
      return Object.keys(errorBody).reduce((acc, k) => {
        let e;
        if (Array.isArray(errorBody[k])) {
          // error type: FormCompatibleError<string[]>
          if (typeof errorBody[k][0] === 'string') {
            e = errorBody[k] as string[];
            return [...acc, ...e];
          }
          // error type: FormCompatibleError<JSONApiErrors>
          e = errorBody[k] as JSONApiErrors;
          const codes = e.map((e) => e[key]);
          return [...acc, ...codes];
        } else {
          // error type: FormCompatibleError<string>
          if (typeof errorBody[k] === 'string') {
            return [...acc, errorBody[k]];
          }
          // error type: FormCompatibleError<JSONApiError>
          e = errorBody[k] as JSONApiError;
          return [...acc, e[key]];
        }
      }, []);
    }
  } catch (e) {
    console.warn('Error body could not be parsed', errorBody);
  }
  console.warn('Error body could not be recognized', errorBody);
  return ['Unknown error - please try again.'];
};

export const getErrorCodes = (errorBody: ErrorTypes) =>
  getErrorData(errorBody, 'code');

// check if an error body matches a certain error code
export const hasError = (errorBody: ErrorTypes, errorCode) => {
  if (!errorBody) return false;
  return !!getErrorCodes(errorBody)?.find((c) => c === errorCode);
};

export const errorStatusToCode = () => undefined;

// parse json api errors to be compatible with redux form
// TODO: move to different layer maybe
// TODO: parse all errors to normalized JSONApi errors
export const parseJSONAPIErrors = (
  errors: JSONApiErrors
): FormCompatibleError<JSONApiError[] | string> => {
  try {
    const fce = errors.reduce<FormCompatibleError<JSONApiError[]>>(
      (acc, val) => {
        if (!val) {
          return acc;
        }

        const field =
          val.source && val.source.pointer !== '/'
            ? val.source.pointer.split('/').reverse()[0]
            : '_error';
        acc[field] = acc[field] || [];

        return {
          ...acc,
          [field]: [
            ...acc[field],
            {
              code: val.code,
              title: val.title,
              detail: val.detail,
              status: val.status,
              source: val.source,
              meta: val.meta
            }
          ]
        };
      },
      {}
    );
    return fce;
  } catch (e) {
    console.warn('Error parsing JSONApi errors', e);
    return {
      _error: 'Parsing errors failed'
    };
  }
};
